import * as React from "react";
import { LocalStorage } from '@lib/localStorage';

export const SAVE_SESSION_DATA = 'SAVE_SESSION_DATA';
export const CLEAR_SESSION_DATA = 'CLEAR_SESSION_DATA';

const storeLocalStorage = new LocalStorage()

type Action =
 | { type: typeof SAVE_SESSION_DATA, key: keyof StoreState, value: ValueOf<StoreState> }
 | { type: typeof CLEAR_SESSION_DATA };

export type StoreState = {
  age: string
  address_state: string
  address_street: string
  address_street_full: string
  address_city: string
  address_county: string
  address_zip: string
  coverage: string
  didViewDetails: string
  dob_day: string
  dob_month: string
  dob_year: string
  date_of_birth: string
  effective_date: Date | null
  email: string
  password: string
  full_name: string
  first_name: string
  gender: string
  last_name: string
  phone_number: string
  product: string
  referrer: string
  riskLevel: 'moderate'
  riskScore: number
  income_estimated: string
  income_household_estimated: string
  mortgage_estimated: string
  existing_policy_life: string
  tobacco: boolean
  userId?: string
  leadId?: string
  quizVersion: string
  currentUser: User | null
  health_quiz: 'yes' | 'no' | undefined
  abConfig: {
    user_set_password: boolean
    show_question_count: boolean
    isDefault: boolean
  }
  manualSurvey: {
    [key: string]: any
  }
  quotes: Quote[]
  height: string
  weight: string
  term: string
  activity_verification?: 'yes' | 'no'
  best_time_to_call?: 'morning' | 'afternoon/evening'
  consultation_date?: string
}

const initialState: StoreState = {
  riskScore: 50,
  riskLevel: 'moderate',
  age: '',
  address_zip: '',
  address_state: '',
  address_street: '',
  address_street_full: '',
  address_city: '',
  address_county: '',
  date_of_birth: '',
  dob_month: '',
  dob_day: '',
  dob_year: '',
  effective_date: null,
  gender: '',
  full_name: '',
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  phone_number: '',
  referrer: '',
  quizVersion: 'v1',
  health_quiz: undefined,
  abConfig: {
    user_set_password: false,
    show_question_count: true,
    isDefault: true
  },
  coverage: '',
  didViewDetails: '',
  product: '',
  income_estimated: '',
  income_household_estimated: '',
  mortgage_estimated: '',
  existing_policy_life: '',
  tobacco: false,
  userId: undefined,
  leadId: undefined,
  manualSurvey: {},
  currentUser: null,
  quotes: [],
  height: '',
  weight: '',
  term: '',
  activity_verification: undefined,
  best_time_to_call: undefined,
  consultation_date: undefined,
}

let cachedState: Partial<StoreState> = {};
cachedState = storeLocalStorage.getAll();
if (cachedState && cachedState.effective_date) {
  cachedState.effective_date = new Date(cachedState.effective_date)
}

export const updateCachedState = (key: keyof StoreState, value: any): void => {
  cachedState[key] = value
}

const GlobalContext = React.createContext<{
  state: StoreState;
  dispatch: React.Dispatch<Action>;
}>({
  state: initialState,
  dispatch: () => null
});

type Reducer<S, A> = (prevState: S, action: A) => S;

const reducer: Reducer<StoreState, Action> = (state: StoreState = initialState, action: Action): StoreState => {
  switch(action.type) {
    case SAVE_SESSION_DATA:
      const newState: StoreState = { ...state };

      if (action.value === null) {
        delete newState[action.key];
      } else {
        newState[action.key as any] = action.value;
      }

      return newState;
    case CLEAR_SESSION_DATA:
      storeLocalStorage.removeAll();
      return initialState;
    default:
      throw new Error();
  };
}

const StateProvider = ({ children }) => {
  const [state, dispatch] = React.useReducer<Reducer<StoreState, Action>>(
    reducer,
    {
      ...initialState,
      ...cachedState
    }
  );

  React.useEffect(() => {
    storeLocalStorage.updateAll(state)
  }, [state]);

  return (
    <GlobalContext.Provider value={{ state, dispatch }}>
      {children}
    </GlobalContext.Provider>
  );
};

export { GlobalContext, StateProvider }
